import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity, TrendingUp, Calendar, ArrowLeft } from 'lucide-react';
import CyberNav from './CyberNav';
import TraceHistory from './TraceHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Stats() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const date = new Date(data.date);
      const formattedDate = date.toLocaleDateString('tr-TR', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });

      return (
        <div 
          className="bg-white dark:bg-zinc-900 border border-cyan-600/30 dark:border-cyan-400/30 rounded-lg px-4 py-3 shadow-xl"
          data-testid="stats-tooltip"
        >
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-1">{formattedDate}</p>
          <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">
            {data.total.toLocaleString()} requests
          </p>
          <div className="mt-2 text-xs text-zinc-600 dark:text-zinc-400 space-y-1">
            <div>Traces: {data.traces}</div>
            <div>Leads: {data.leads}</div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Format date for X axis
  const formatXAxis = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-zinc-950 transition-colors">
        <CyberNav 
          onSearch={() => navigate('/')} 
          traceState="idle" 
          onOpenHistory={() => setShowHistory(true)} 
        />
        <div className="pt-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 dark:border-cyan-400"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-zinc-950 transition-colors">
        <CyberNav 
          onSearch={() => navigate('/')} 
          traceState="idle" 
          onOpenHistory={() => setShowHistory(true)} 
        />
        <div className="pt-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-20">
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={fetchStats}
                className="mt-4 px-4 py-2 bg-cyan-600/10 dark:bg-cyan-600/10 border border-cyan-600/30 dark:border-cyan-600/30 rounded-lg text-cyan-700 dark:text-cyan-400 hover:bg-cyan-600/20 dark:hover:bg-cyan-600/20 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 transition-colors noise-overlay">
      <CyberNav 
        onSearch={() => navigate('/')} 
        traceState="idle" 
        onOpenHistory={() => setShowHistory(true)} 
      />
      
      {/* Trace History Modal */}
      {showHistory && (
        <TraceHistory 
          isOpen={showHistory} 
          onClose={() => setShowHistory(false)} 
          onReplay={(target) => {
            setShowHistory(false);
            navigate('/', { state: { autoTrace: target } });
          }}
        />
      )}
      
      <main className="pt-20 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Back button + Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 mb-4 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
              data-testid="back-to-home-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
            <h1 className="text-4xl font-bold text-zinc-900 dark:text-white mb-2">
              Open Stats
            </h1>
            <p className="text-zinc-600 dark:text-zinc-400">
              Transparency about whatismyroute.com usage. Below are public metrics including API traffic.
            </p>
          </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div 
            className="bg-zinc-100 dark:bg-zinc-900/50 border border-zinc-300 dark:border-white/10 rounded-xl p-6"
            data-testid="total-requests-card"
          >
            <div className="flex items-center gap-3 mb-2">
              <Activity className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              <span className="text-sm text-zinc-600 dark:text-zinc-400">Total Requests</span>
            </div>
            <p className="text-3xl font-bold text-zinc-900 dark:text-white">
              {stats?.summary?.total_requests?.toLocaleString() || 0}
            </p>
          </div>

          <div 
            className="bg-zinc-100 dark:bg-zinc-900/50 border border-zinc-300 dark:border-white/10 rounded-xl p-6"
            data-testid="total-traces-card"
          >
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm text-zinc-600 dark:text-zinc-400">Traces</span>
            </div>
            <p className="text-3xl font-bold text-zinc-900 dark:text-white">
              {stats?.summary?.total_traces?.toLocaleString() || 0}
            </p>
          </div>

          <div 
            className="bg-zinc-100 dark:bg-zinc-900/50 border border-zinc-300 dark:border-white/10 rounded-xl p-6"
            data-testid="unique-visitors-card"
          >
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <span className="text-sm text-zinc-600 dark:text-zinc-400">Unique Visitors</span>
            </div>
            <p className="text-3xl font-bold text-zinc-900 dark:text-white">
              {stats?.summary?.unique_visitors?.toLocaleString() || 0}
            </p>
          </div>
        </div>

        {/* Chart */}
        <div 
          className="bg-zinc-100 dark:bg-zinc-900/50 border border-zinc-300 dark:border-white/10 rounded-xl p-6 outline-none focus:outline-none"
          data-testid="stats-chart-container"
          tabIndex={-1}
        >
          <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
            API Traffic
          </h2>
          <p className="text-zinc-600 dark:text-zinc-400 mb-6">
            Last 30 days traffic - whatismyroute.com
          </p>

          <div className="h-[400px] w-full outline-none focus:outline-none">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={stats?.daily || []}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="rgba(100,116,139,0.2)" 
                  vertical={false}
                />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatXAxis}
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  tickLine={false}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#06b6d4" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorTotal)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </main>
    </div>
  );
}
